/* ============================================================================
   VILLAINS TCG — Cart Drawer interactions (cst-cart-drawer.js)
   Opens via the shared overlay manager (window.CST.overlay). Line quantity /
   remove use the Cart AJAX API with the Section Rendering API to re-render the
   drawer server-side (single source of truth). Events are delegated so the
   handlers survive content swaps. Exposes window.CST.cart = { open, refresh }.
   ========================================================================== */

const Base = (window.CST && window.CST.Element) || HTMLElement;

class CstCartDrawer extends Base {
  mount() {
    this.sectionId = this.dataset.sectionId;
    this.routes = (window.Theme && window.Theme.routes) || {};

    this.listen(this, 'click', (e) => this.onClick(e));
    this.listen(document, 'cst:cart:open', (e) => this.open(e.detail && e.detail.trigger));
    this.listen(document, 'cst:cart:refresh', () => this.refresh(true));

    window.CST = Object.assign(window.CST || {}, {
      cart: { open: (t) => this.open(t), refresh: (open) => this.refresh(open) },
    });
  }

  open(trigger) {
    if (window.CST && window.CST.overlay) window.CST.overlay.open(this, trigger, { backdrop: true });
    else { this.hidden = false; requestAnimationFrame(() => this.classList.add('is-open')); }
  }

  onClick(e) {
    if (this.classList.contains('is-busy')) { e.preventDefault(); return; } // ignore during in-flight change
    const up = e.target.closest('[data-cst-qty-up]');
    const down = e.target.closest('[data-cst-qty-down]');
    const remove = e.target.closest('[data-cst-remove]');
    if (!up && !down && !remove) return;

    const btn = up || down || remove;
    const key = btn.dataset.key;
    const item = btn.closest('[data-cst-line-item]');
    const current = item ? parseInt(item.querySelector('[data-cst-qty-val]')?.textContent, 10) || 0 : 0;

    let quantity = current;
    if (up) quantity = current + 1;
    else if (down) quantity = Math.max(0, current - 1);
    else if (remove) quantity = 0;

    // Remember what to re-focus after the DOM swap.
    const focusAction = up ? 'up' : down ? 'down' : 'remove';
    e.preventDefault();
    this.changeLine(key, quantity, focusAction);
  }

  async changeLine(key, quantity, focusAction) {
    this.classList.add('is-busy');
    try {
      const res = await fetch(this.routes.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: this.sectionId, sections_url: window.location.pathname }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        this.announce(data.description || data.message || 'Could not update your cart.');
        return;
      }
      this.renderFromSections(data.sections);
      if (typeof data.item_count === 'number') this.updateCount(data.item_count);
      this.restoreFocus(key, focusAction);
      this.announce(`Cart updated${typeof data.item_count === 'number' ? `, ${data.item_count} items` : ''}.`);
      this.emit('cst:cart:changed', { item_count: data.item_count });
    } catch (_) {
      this.announce('Something went wrong updating your cart.');
    } finally {
      this.classList.remove('is-busy');
    }
  }

  async refresh(openAfter = false) {
    try {
      const res = await fetch(`${window.location.pathname}?sections=${this.sectionId}`);
      if (!res.ok) return;
      const data = await res.json();
      this.renderFromSections(data);
      const n = parseInt(this.querySelector('.cst-cart-drawer__count')?.textContent, 10);
      if (!Number.isNaN(n)) this.updateCount(n);
      if (openAfter) this.open();
    } catch (_) { /* noop */ }
  }

  renderFromSections(sections) {
    const html = sections && sections[this.sectionId];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const newContent = doc.querySelector('[data-cst-cart-content]');
    const curContent = this.querySelector('[data-cst-cart-content]');
    if (newContent && curContent) curContent.replaceWith(newContent);

    const newFoot = doc.querySelector('.cst-cart-drawer__foot');
    const curFoot = this.querySelector('.cst-cart-drawer__foot');
    if (newFoot && curFoot) curFoot.replaceWith(newFoot);
    else if (newFoot && !curFoot) this.appendChild(newFoot);
    else if (!newFoot && curFoot) curFoot.remove();

    const newCount = doc.querySelector('.cst-cart-drawer__count');
    const curCount = this.querySelector('.cst-cart-drawer__count');
    if (newCount && curCount) curCount.textContent = newCount.textContent;
  }

  restoreFocus(key, action) {
    const sel = action === 'up' ? '[data-cst-qty-up]' : action === 'down' ? '[data-cst-qty-down]' : '[data-cst-remove]';
    const item = this.querySelector(`[data-cst-line-item][data-key="${CSS.escape(key)}"]`);
    const target = (item && item.querySelector(sel))
      || (item && item.querySelector('[data-cst-qty-up]'))
      || this.querySelector('[data-cst-close]');
    target?.focus?.({ preventScroll: true });
  }

  announce(msg) {
    const region = this.querySelector('[data-cst-cart-status]');
    if (region) region.textContent = msg;
  }

  updateCount(n) {
    const bubble = document.querySelector('[data-cst-cart-count]');
    if (bubble) { bubble.textContent = n; bubble.classList.toggle('is-empty', !n); }
    const trigger = document.querySelector('[data-cst-open-cart]');
    if (trigger) trigger.setAttribute('aria-label', `Cart, ${n} items`);
  }
}

if (window.CST && window.CST.defineElement) window.CST.defineElement('cst-cart-drawer', CstCartDrawer);
else if (!customElements.get('cst-cart-drawer')) customElements.define('cst-cart-drawer', CstCartDrawer);
